import { useCallback, useEffect, useState } from 'react';

import {
  createProductUnitOfMeasure,
  deleteProductUnitOfMeasure,
  getProductUnitsOfMeasure,
  getUomTypes,
  updateProductUnitOfMeasure,
} from '../../../../../store/products/unit-of-measure.api';

import type { PersistPayload, ProductUnitOfMeasureResponse, UomTypeResponse } from './uom-types';

export type { PersistPayload };

type UseProductUomResult = {
  items: ProductUnitOfMeasureResponse[];
  uomTypes: UomTypeResponse[];
  isLoading: boolean;
  hasError: boolean;
  reload: () => Promise<void>;
  create: (payload: PersistPayload) => Promise<void>;
  update: (productUnitOfMeasureId: number, payload: PersistPayload) => Promise<void>;
  remove: (productUnitOfMeasureId: number) => Promise<void>;
};

export const useProductUom = (productId: number | null): UseProductUomResult => {
  const [items, setItems] = useState<ProductUnitOfMeasureResponse[]>([]);
  const [uomTypes, setUomTypes] = useState<UomTypeResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const reload = useCallback(async () => {
    if (!productId) return;
    setItems([]);
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await getProductUnitsOfMeasure(productId);
      setItems(data);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    let cancelled = false;
    getUomTypes()
      .then((types) => {
        if (cancelled) return;
        setUomTypes(types);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const create = useCallback(
    async (payload: PersistPayload) => {
      if (!productId) return;
      await createProductUnitOfMeasure(productId, payload);
      await reload();
    },
    [productId, reload],
  );

  const update = useCallback(
    async (productUnitOfMeasureId: number, payload: PersistPayload) => {
      if (!productId) return;
      await updateProductUnitOfMeasure(productId, productUnitOfMeasureId, payload);
      await reload();
    },
    [productId, reload],
  );

  const remove = useCallback(
    async (productUnitOfMeasureId: number) => {
      if (!productId) return;
      await deleteProductUnitOfMeasure(productId, productUnitOfMeasureId);
      await reload();
    },
    [productId, reload],
  );

  return { items, uomTypes, isLoading, hasError, reload, create, update, remove };
};
